'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import React from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { cn } from '@src/utils/tailwind'

interface ProductInfoProps {
  title: string
  description?: string
  finalPrice: string
  strikedPrice?: string
  accolades?: Accolade[]
  socialProof?: SocialProof[]
  alternateProducts?: AlternateProduct[]
}

export const ProductInfo = ({
  title,
  description,
  finalPrice,
  strikedPrice,
  accolades = [],
  socialProof = [],
  alternateProducts = [],
}: ProductInfoProps) => {
  const pathname = usePathname()
  return (
    <div>
      {accolades.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {accolades?.map((accolade) => (
            <div
              key={accolade.content}
              className="prose flex h-7 items-center justify-center rounded-sm bg-bodi-gray-pearl px-[10px] font-medium text-bodi-green-deep-500"
              dangerouslySetInnerHTML={{ __html: accolade.content }}
            ></div>
          ))}
        </div>
      )}
      <h1 className="text-heading-display-2">{title}</h1>
      <div className="pb-4">
        {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
      </div>
      <div className="flex items-center justify-between lg:justify-normal lg:gap-8">
        <div className="flex gap-2 font-medium">
          {strikedPrice && (
            <span
              className={cn('text-body-xl', {
                'line-through text-bodi-gray-disabled': !!strikedPrice,
              })}
            >
              {strikedPrice}
            </span>
          )}
          <span className={cn('text-body-xl', { 'text-bodi-black-text': !!strikedPrice })}>
            {finalPrice}
          </span>
        </div>
        {socialProof?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {socialProof.map((socialProof) => (
              <div key={socialProof.content} className="flex items-center gap-2">
                <Image
                  src={socialProof.image?.src ?? ''}
                  width={socialProof?.image?.width}
                  height={socialProof?.image?.height}
                  alt={socialProof?.image?.alt}
                />
                <div
                  className="prose text-body-sm font-medium text-bodi-black-text"
                  dangerouslySetInnerHTML={{ __html: socialProof?.content }}
                ></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {alternateProducts?.length > 0 && (
        <div className="-mb-2 mt-6 flex lg:mt-8">
          {alternateProducts?.map((product) => {
            const isCurrentProduct = product.url === getUnlocalizedPath(pathname)
            return (
              <LocalizedLink
                key={product.url}
                href={product.url}
                className={cn(
                  'flex flex-1 flex-wrap items-center justify-center gap-1 border px-5 py-2 text-center text-body-lg transition-all',
                  'first:rounded-l-lg first:border-r-0 last:rounded-r-lg last:border-l-0',
                  {
                    'bg-bodi-black-text text-white border-bodi-black-text': isCurrentProduct,
                    'border-bodi-gray-butters bg-white text-bodi-black-text hover:bg-bodi-gray-pearl':
                      !isCurrentProduct,
                  }
                )}
              >
                <span className="prose" dangerouslySetInnerHTML={{ __html: product.name }}></span>
                {product?.descriptor && (
                  <>
                    <span>-</span>
                    <span
                      className={cn('text-body-sm font-medium text-bodi-green-deep-500', {
                        'text-bodi-green-bright-500': isCurrentProduct,
                      })}
                    >
                      {product.descriptor}
                    </span>
                  </>
                )}
              </LocalizedLink>
            )
          })}
        </div>
      )}
    </div>
  )
}
